import { useCallback, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Tooltip,
  Stack,
  Typography,
} from "@mui/material"
import ArrowBackIcon from "@mui/icons-material/ArrowBackOutlined"
import ChatIcon from "@mui/icons-material/ForumOutlined"
import AddIcon from "@mui/icons-material/AddOutlined"
import ExpandIcon from "@mui/icons-material/ChevronRightOutlined"
import CopyIcon from "@mui/icons-material/ContentCopyOutlined"

import { projectApi, taskApi } from "../api"
import { usePermissions } from "../permissions"
import PageHeader from "../components/PageHeader"
import { LoadingScreen } from "../components/LoadingState"
import TaskDetailDrawer from "./tasks/TaskDetailDrawer"
import TaskDialog from "./tasks/TaskDialog"
import { useTimeSummary } from "../time/useTimeSummary"
import { decimalHours } from "../time/duration"
import TimeBreakdown, { TimeFigure } from "../time/TimeBreakdown"
import { applyOrder, useDragReorder } from "../components/useDragReorder"
import DragIcon from "@mui/icons-material/DragIndicator"
import { avatarColor, formatDate, initialsOf } from "../utils/format"
import { PRIORITY_COLORS, STATUS_FALLBACK_COLORS } from "../theme"
import { filledChipSx } from "../theme/contrast"
import { MOTION, transition } from "../theme"
import SubtaskList from "./tasks/SubtaskList"
import CopyProjectDialog from "./projects/CopyProjectDialog"

export default function ProjectDetailPage() {
  const { projectId } = useParams()
  const navigate = useNavigate()

  const [data, setData] = useState(null)
  const [tasks, setTasks] = useState([])
  const [statuses, setStatuses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [openTaskId, setOpenTaskId] = useState(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [copyOpen, setCopyOpen] = useState(false)
  /* Which tasks are showing their steps. An object rather than one id: the
     list is a checklist of checklists, and closing one to open another is a
     click people did not ask to spend. */
  const [expanded, setExpanded] = useState({})
  const [subtasks, setSubtasks] = useState({})
  const { can } = usePermissions()

  /* Per task, so the list can carry a figure per row and the header their sum —
     one request rather than one per task. */
  /* `top_task`, so each row's figure includes the time logged on its steps —
     with `task` a step's hours sat on a row this list does not show. */
  const time = useTimeSummary({ projectId: Number(projectId), groupBy: "top_task" })
  /* Which figure's "who logged this" is open: the project's, or one task's. */
  const [breakdown, setBreakdown] = useState(null)

  /*
   * The tasks in the order somebody drags them into.
   *
   * Shown at once and saved behind it; a save that fails puts the list back
   * the way it was and says so, rather than leaving an order on screen that
   * the next visit will not have.
   */
  const [reorderError, setReorderError] = useState(null)
  const reorderTasks = async (ids) => {
    const previous = tasks
    setReorderError(null)
    setTasks((current) => applyOrder(current, ids))
    try {
      await taskApi.reorder(ids)
    } catch (err) {
      setTasks(previous)
      setReorderError(err.userMessage || "That order could not be saved.")
    }
  }
  const taskDrag = useDragReorder(tasks, { onReorder: reorderTasks, enabled: can("tasks", "update") })

  /*
   * One task's steps, fetched when it is first opened.
   *
   * Not with the list: a project of forty tasks would be forty extra queries
   * for rows most people never look at, and the count that decides whether the
   * arrow is worth pressing is already on the parent row.
   */
  const toggle = useCallback(
    async (task) => {
      const key = String(task.id)
      setExpanded((current) => ({ ...current, [key]: !current[key] }))
      if (subtasks[key]) return
      try {
        const payload = await taskApi.list({ parent_id: task.id, limit: 100 })
        setSubtasks((current) => ({ ...current, [key]: payload.tasks || [] }))
      } catch {
        setSubtasks((current) => ({ ...current, [key]: [] }))
      }
    },
    [subtasks],
  )

  /*
   * Which tasks are refetching their steps, so the checklist can say so over
   * itself instead of being replaced by a loader. Emptying `subtasks[key]`
   * would have done the refetch too — and taken the rows off the screen for
   * the length of the request, which is the flicker this exists to avoid.
   */
  const [stepsBusy, setStepsBusy] = useState({})

  /*
   * The project and its top-level tasks.
   *
   * ── Why it can refetch without saying so ────────────────────────────────
   *
   * The page shows a loading screen INSTEAD of itself while `loading` is set,
   * which is right the first time and wrong every time after it: ticking a step
   * inside a task took the whole project away and brought it back, when the
   * only thing that had changed was one count on one row. Called with `quiet`
   * the fetch happens with the page still on screen, and whatever asked for it
   * shows its own, smaller sign that something is in flight.
   */
  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true)
    try {
      const [projectData, taskData, statusData] = await Promise.all([
        projectApi.get(projectId),
        /* Top level only. A project of ten tasks with forty steps inside them
           is a list of fifty rows that no longer says how much work there is;
           the steps arrive under the task they belong to. */
        taskApi.list({ project_id: projectId, top_level: "true", limit: 200 }),
        taskApi.statuses(),
      ])
      setData(projectData)
      setTasks(taskData.tasks || [])
      setStatuses(statusData.statuses || [])
    } catch (err) {
      setError(err.userMessage)
    } finally {
      if (!quiet) setLoading(false)
    }
  }, [projectId])

  /*
   * One task's steps again, after something inside them changed.
   *
   * The rows are swapped when the new ones arrive, never before: the list keeps
   * its height and its scroll position, and the only thing that moves is the
   * row that was ticked. The project reloads alongside it — quietly — because
   * the task's own "2 of 5" lives on the parent row.
   */
  const refreshSteps = useCallback(async (taskId) => {
    const key = String(taskId)
    setStepsBusy((current) => ({ ...current, [key]: true }))
    try {
      const payload = await taskApi.list({ parent_id: taskId, limit: 100 })
      setSubtasks((current) => ({ ...current, [key]: payload.tasks || [] }))
    } catch {
      /* Leave the rows showing what they were. A failed refresh is not a
         reason to empty a list somebody is reading. */
    } finally {
      setStepsBusy((current) => {
        const rest = { ...current }
        delete rest[key]
        return rest
      })
    }
  }, [])

  /*
   * Everything a change inside a task can affect: its steps, and the counts on
   * the row above them. Both quietly, both at once.
   */
  const refreshTask = useCallback(
    async (taskId) => {
      await Promise.all([refreshSteps(taskId), load(true)])
    },
    [refreshSteps, load],
  )

  /*
   * The same, for whichever tasks happen to be open.
   *
   * A step ticked inside the drawer is a step in a list this page may also be
   * showing behind it, and leaving that copy as it was means closing the drawer
   * onto a checklist that disagrees with the one just used. Only the expanded
   * tasks are refetched: the rest are not on screen to be wrong.
   */
  const refreshOpenTasks = useCallback(async () => {
    const open = Object.keys(expanded).filter((key) => expanded[key])
    await Promise.all([load(true), ...open.map((key) => refreshSteps(key))])
  }, [expanded, load, refreshSteps])

  useEffect(() => {
    load()
  }, [load])

  if (loading) return <LoadingScreen minHeight={400} />
  if (!data) {
    return (
      <Box>
        <Alert severity="error">{error || "This project is not available."}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/projects")} sx={{ mt: 2 }}>
          Back to projects
        </Button>
      </Box>
    )
  }

  const { project, members, chat_rooms: chatRooms } = data
  const secondsByTask = new Map(time.groups.map((g) => [String(g.group_key), Number(g.tracked_seconds) || 0]))
  const done = tasks.filter((t) => t.is_terminal).length
  const progress = tasks.length > 0 ? Math.round((done / tasks.length) * 100) : 0

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/projects")} size="small" sx={{ mb: 1 }}>
        Projects
      </Button>

      <PageHeader
        title={project.name}
        subtitle={
          [project.code, project.client_id !== "0" ? project.client_label : null, project.description]
            .filter(Boolean)
            .join(" · ")
        }
        actions={
          can("projects", "create") ? (
            <Button variant="outlined" startIcon={<CopyIcon />} onClick={() => setCopyOpen(true)}>
              Duplicate
            </Button>
          ) : null
        }
      />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                <Typography variant="h3" sx={{ flex: 1 }}>
                  Tasks
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {done} of {tasks.length} done
                </Typography>
                {/* Raising a task from the project it belongs to, rather than
                    going to Tasks and picking this project back out of a list. */}
                {can("tasks", "create") ? (
                  <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
                    New task
                  </Button>
                ) : null}
              </Stack>

              {tasks.length > 0 ? <LinearProgress variant="determinate" value={progress} sx={{ height: 5, borderRadius: 3, mb: 2 }} /> : null}

              {tasks.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                  No tasks in this project yet.
                </Typography>
              ) : (
                <Stack divider={<Divider />}>
                  {reorderError ? (
                    <Alert severity="error" onClose={() => setReorderError(null)} sx={{ mb: 1 }}>
                      {reorderError}
                    </Alert>
                  ) : null}
                  {tasks.map((task) => (
                    <Box key={task.id} sx={{ py: 1.25 }}>
                      {/* The row is what is picked up — not the box around it,
                          which holds the steps, so a step dragged inside its
                          own list never lifts the task with it. */}
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        onClick={() => setOpenTaskId(task.id)}
                        {...taskDrag.rowProps(task)}
                        sx={{
                          cursor: "pointer",
                          borderRadius: 1,
                          "&:hover": { bgcolor: "action.hover" },
                          "&:hover .task-drag, & .task-drag:focus-visible": { opacity: 1 },
                          ...taskDrag.rowSx(task),
                        }}
                      >
                        {can("tasks", "update") ? (
                          <Tooltip title="Drag to reorder · or focus and use ↑ ↓">
                            <IconButton
                              size="small"
                              className="task-drag"
                              aria-label={`Move ${task.task_code}`}
                              {...taskDrag.handleProps(task)}
                              sx={{ p: 0.25, cursor: "grab", opacity: 0.35, transition: transition("opacity", MOTION.fast) }}
                            >
                              <DragIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        {/* The arrow is its own target. The row opens the task;
                            expanding it is a different question, and a row that
                            did both would answer neither predictably. */}
                        <Tooltip title={task.subtask_total > 0 ? "Show its steps" : "No steps yet"}>
                          <Box component="span" sx={{ display: "inline-flex" }}>
                            <IconButton
                              size="small"
                              disabled={!task.subtask_total}
                              aria-label={`${expanded[String(task.id)] ? "Hide" : "Show"} the steps in ${task.task_code}`}
                              aria-expanded={Boolean(expanded[String(task.id)])}
                              onClick={(event) => {
                                event.stopPropagation()
                                toggle(task)
                              }}
                              sx={{
                                p: 0.25,
                                transform: expanded[String(task.id)] ? "rotate(90deg)" : "none",
                                transition: transition("transform", MOTION.fast),
                              }}
                            >
                              <ExpandIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Box>
                        </Tooltip>
                        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: PRIORITY_COLORS[task.priority], flexShrink: 0 }} />
                        <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary", flexShrink: 0 }}>
                          {task.task_code}
                        </Typography>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ flex: 1, textDecoration: task.is_terminal ? "line-through" : "none", opacity: task.is_terminal ? 0.6 : 1 }}
                        >
                          {task.title}
                        </Typography>
                        {/* Only where there is time. A column of dashes down a
                            project nobody has tracked against is noise; a figure
                            appearing on the rows that have one is the signal. */}
                        <TimeFigure
                          seconds={secondsByTask.get(String(task.id))}
                          label={`Who logged time on ${task.task_code}`}
                          onOpen={(anchor) =>
                            setBreakdown({
                              anchor,
                              title: `${task.task_code} · ${task.title}`,
                              query: { task_id: task.id, with_steps: "true" },
                            })
                          }
                          sx={{ fontSize: 12, color: "text.secondary", flexShrink: 0 }}
                        />
                        {/* How much of this task is actually finished, on the
                            row rather than inside it. "Completed 0/5" is the
                            thing somebody scanning a project is looking for. */}
                        {task.subtask_total > 0 ? (
                          <Chip
                            size="small"
                            label={`${task.subtask_done}/${task.subtask_total} done`}
                            color={task.subtask_done === task.subtask_total ? "success" : "default"}
                            variant={task.subtask_done === task.subtask_total ? "filled" : "outlined"}
                            sx={{ height: 20, fontSize: 10, flexShrink: 0 }}
                          />
                        ) : null}
                        <Chip
                          size="small"
                          label={task.status_label}
                          sx={{
                            height: 20,
                            fontSize: 10,
                            flexShrink: 0,
                            ...filledChipSx(task.status_color || STATUS_FALLBACK_COLORS[task.status_name]),
                          }}
                        />
                      </Stack>

                      {/* Collapse rather than a conditional: rows appearing
                          instantly shove everything below them out from under
                          the reader. */}
                      <Collapse in={Boolean(expanded[String(task.id)])} timeout={200} unmountOnExit>
                        <Box sx={{ pl: 4.5, pr: 1, pt: 1 }}>
                          <SubtaskList
                            task={task}
                            subtasks={subtasks[String(task.id)] || []}
                            statuses={statuses}
                            dense
                            onOpen={setOpenTaskId}
                            refreshing={Boolean(stepsBusy[String(task.id)])}
                            onChanged={() => refreshTask(task.id)}
                          />
                        </Box>
                      </Collapse>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h3" gutterBottom>
                Details
              </Typography>
              <Stack spacing={1}>
                <Row label="Status" value={project.status.replace("_", " ")} capitalize />
                <Row label="Owner" value={project.owner_name} />
                <Row label="Start" value={formatDate(project.start_date)} />
                <Row label="Due" value={formatDate(project.due_date)} />
                {/* The heading is whatever the viewer is actually being shown:
                    without view_all these are their own hours, and calling that
                    "Time logged" on a project three people work on is a lie. */}
                {time.mayView ? (
                  <Row
                    label={time.label}
                    value={
                      time.trackedSeconds > 0 ? (
                        <>
                          <TimeFigure
                            seconds={time.trackedSeconds}
                            label="Who logged time on this project"
                            onOpen={(anchor) => setBreakdown({ anchor, title: project.name, query: { project_id: project.id } })}
                          />
                          {` · ${decimalHours(time.trackedSeconds)} h`}
                        </>
                      ) : (
                        "None yet"
                      )
                    }
                  />
                ) : null}
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h3" gutterBottom>
                Members
              </Typography>
              <Stack spacing={1.25}>
                {members.map((member) => (
                  <Stack key={member.user_id} direction="row" spacing={1.25} alignItems="center">
                    <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: avatarColor(member.name || member.email) }}>
                      {initialsOf(member.name || member.email)}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" noWrap>
                        {member.name || member.email}
                      </Typography>
                    </Box>
                    <Chip size="small" label={member.role} sx={{ height: 18, fontSize: 10 }} />
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>

          {/* The chat↔work link, from the project side. */}
          {chatRooms.length > 0 ? (
            <Card>
              <CardContent>
                <Typography variant="h3" gutterBottom>
                  Conversations
                </Typography>
                <Stack spacing={0.5}>
                  {chatRooms.map((room) => (
                    <Button
                      key={room.id}
                      size="small"
                      startIcon={<ChatIcon fontSize="small" />}
                      onClick={() => navigate(`/chat/${room.id}`)}
                      sx={{ justifyContent: "flex-start" }}
                    >
                      {room.topic || room.title || "Conversation"}
                    </Button>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          ) : null}
        </Grid>
      </Grid>

      <CopyProjectDialog
        project={copyOpen ? project : null}
        onClose={() => setCopyOpen(false)}
        onCopied={() => setCopyOpen(false)}
      />

      <TaskDialog
        open={createOpen}
        statuses={statuses}
        projects={[]}
        clients={[]}
        project={project}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false)
          load()
        }}
      />

      <TimeBreakdown
        anchorEl={breakdown?.anchor || null}
        title={breakdown?.title}
        query={breakdown?.query}
        onClose={() => setBreakdown(null)}
      />

      <TaskDetailDrawer
        taskId={openTaskId}
        statuses={statuses}
        onClose={() => setOpenTaskId(null)}
        /* Quietly: the drawer is open over this page, and taking the page out
           from under it to redraw a count is a flash around the edges of
           whatever the person is actually reading. */
        onChanged={refreshOpenTasks}
      />
    </Box>
  )
}

/**
 * @param {boolean} [capitalize] For a value that arrives as a database token —
 *   `on_hold` becoming "On Hold". Opt-in, because CSS capitalize hits every word
 *   it can find, and on a value like "8h 1m · 8.02 h" it silently shouts the
 *   unit back as "8.02 H".
 */
function Row({ label, value, capitalize = false }) {
  return (
    <Stack direction="row" spacing={2}>
      <Typography variant="body2" color="text.secondary" sx={{ width: 92, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ textTransform: capitalize ? "capitalize" : "none", minWidth: 0 }}>
        {value || "—"}
      </Typography>
    </Stack>
  )
}
